<script setup lang="ts">
import { ref, shallowRef, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';
import { getComponentsAsync } from './componentRegistry';
import RawViewTags from './RawViewTags.vue';

const { t } = useI18n();

interface ReferenceUnitHandle {
    type: string;
    reference_unit: string;
    display_identifier?: string;
    content_hash?: string;
    metadata?: Record<string, unknown>;
}

const props = defineProps<{
    references: ReferenceUnitHandle[];
    initialIndex: number;
    visible: boolean;
    asPanel?: boolean;
    hideHeader?: boolean;
}>();

const emit = defineEmits<{ close: [] }>();

const matterStore = useMatterStore();

const currentIndex = ref(props.initialIndex);

watch(
    () => props.initialIndex,
    (newIndex) => {
        currentIndex.value = newIndex;
    },
);

const currentReference = computed<ReferenceUnitHandle | null>(() => {
    if (currentIndex.value >= 0 && currentIndex.value < props.references.length) {
        return props.references[currentIndex.value];
    }
    return null;
});

const hasNavigation = computed(() => props.references.length > 1);
const hasPrev = computed(() => currentIndex.value > 0);
const hasNext = computed(() => currentIndex.value < props.references.length - 1);
const currentPosition = computed(() => {
    if (!hasNavigation.value) return '';
    return `${currentIndex.value + 1} / ${props.references.length}`;
});

const title = computed(() => {
    if (!currentReference.value) return '';

    const refItem = currentReference.value;
    const typeName = refItem.type || t('references.itemFallback');
    const formattedType = typeName.replace(/([a-z])([A-Z])/g, '$1 $2');

    if (refItem.reference_unit === 'ConversationDay') {
        const day = refItem.metadata?.day || t('references.unknownDate');
        return `${formattedType} ${refItem.display_identifier} - ${day}`;
    }

    return `${formattedType} ${refItem.display_identifier}`;
});

const componentToRender = shallowRef<unknown | null>(null);
const loading = ref(true);

async function loadComponent() {
    if (!currentReference.value) {
        componentToRender.value = null;
        loading.value = false;
        return;
    }

    loading.value = true;

    const components = await getComponentsAsync(
        currentReference.value.type,
        currentReference.value.reference_unit,
        matterStore.currentMatter?.RecordId ?? null,
    );

    componentToRender.value = components?.rawView || null;
    loading.value = false;
}

watch(currentReference, loadComponent, { immediate: true });

function navigatePrev() {
    if (hasPrev.value) {
        currentIndex.value--;
    }
}

function navigateNext() {
    if (hasNext.value) {
        currentIndex.value++;
    }
}

function close() {
    emit('close');
}
</script>

<template>
    <div v-if="asPanel && visible" class="raw-view-panel">
        <div v-if="!hideHeader" class="raw-view-panel-header">
            <h3>{{ title }}</h3>
            <div v-if="hasNavigation" class="header-navigation">
                <button
                    :disabled="!hasPrev"
                    class="nav-button nav-button-icon"
                    @click="navigatePrev"
                >
                    <span class="material-icons nav-icon">arrow_back</span>
                </button>
                <span class="nav-position-text">{{ currentPosition }}</span>
                <button
                    :disabled="!hasNext"
                    class="nav-button nav-button-icon"
                    @click="navigateNext"
                >
                    <span class="material-icons nav-icon">arrow_forward</span>
                </button>
            </div>
        </div>

        <div class="raw-view-panel-body">
            <RawViewTags
                v-if="!loading && currentReference"
                :content-hash="currentReference.content_hash"
            />

            <div v-if="loading" class="loading-state">
                <span class="loading-spinner"></span>
            </div>

            <component
                :is="componentToRender"
                v-else-if="componentToRender && currentReference"
                :reference="currentReference"
            />
            <div v-else-if="currentReference" class="error-state">
                <p>
                    {{
                        t('references.noRawViewAvailable', {
                            type: currentReference.type,
                            view: currentReference.reference_unit,
                        })
                    }}
                </p>
            </div>
            <div v-else class="error-state">
                <p>{{ t('references.noReferenceSelected') }}</p>
            </div>
        </div>
    </div>

    <div v-else-if="!asPanel && visible" class="communication-raw-view-overlay" @click.self="close">
        <div class="communication-raw-view-drawer">
            <div class="modal-header">
                <h2>{{ title }}</h2>
                <div class="header-right">
                    <div v-if="hasNavigation" class="header-navigation">
                        <button
                            :disabled="!hasPrev"
                            class="nav-button nav-button-icon"
                            @click="navigatePrev"
                        >
                            <span class="material-icons nav-icon">arrow_back</span>
                        </button>
                        <span class="nav-position-text">{{ currentPosition }}</span>
                        <button
                            :disabled="!hasNext"
                            class="nav-button nav-button-icon"
                            @click="navigateNext"
                        >
                            <span class="material-icons nav-icon">arrow_forward</span>
                        </button>
                    </div>
                    <button class="close-button" @click="close">✕</button>
                </div>
            </div>

            <div class="modal-body">
                <RawViewTags
                    v-if="!loading && currentReference"
                    :content-hash="currentReference.content_hash"
                />

                <div v-if="loading" class="loading-state">
                    <span class="loading-spinner"></span>
                </div>

                <component
                    :is="componentToRender"
                    v-else-if="componentToRender && currentReference"
                    :reference="currentReference"
                />
                <div v-else-if="currentReference" class="error-state">
                    <p>
                        {{
                            t('references.noRawViewAvailable', {
                                type: currentReference.type,
                                view: currentReference.reference_unit,
                            })
                        }}
                    </p>
                </div>
                <div v-else class="error-state">
                    <p>{{ t('references.noReferenceSelected') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
